import { useCallback, useEffect, useState } from 'react';
import type { Paginated, Permission, Role, User } from '@shared/types';
import { ROLES } from '@shared/types';
import { ROLE_DESCRIPTIONS, ROLE_LABELS, permissionsForRole, PERMISSION_LABELS } from '@shared/roles';import { PASSWORD_HINT, validateEmail, validateName, validatePassword } from '@shared/validate';
import { formatDateTime, relativeTime } from '@shared/format';
import { PageContainer, PageHeader } from '../../app/AppShell';
import { useAuthStore } from '../../app/authStore';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '../../components/Card';
import { Select, TextInput } from '../../components/Field';
import { DataTable, type Column } from '../../components/Table';
import { toast } from '../../components/Toast';
import { Modal, overlay } from '../../overlay';
import { api, errorMessage } from '../../lib/api';

/**
 * 账号管理。
 *
 * 平台不开放自助注册：账号由管理员创建，初始口令由管理员告知，首次登录强制改密。
 * 有贡献的账号不允许删除，只能停用 —— 删掉会把别人的讨论一起带走。
 */
export function UsersPage() {
  const currentUser = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.includes('user.manage');

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'member' as Role, password: '' });
  const [temporary, setTemporary] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<Paginated<User>>('/api/users', {
        query: { search: search.trim(), pageSize: 200 },
      });
      setUsers(result.items);
      setError(null);
    } catch (fetchError) {
      setError(errorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  const createUser = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/users', form);
      toast.ok('账号已创建', `请把初始口令告知 ${form.name}，其首次登录会被要求修改。`);
      setCreating(false);
      setForm({ email: '', name: '', role: 'member', password: '' });
      await load();
    } catch (createError) {
      toast.critical('创建失败', errorMessage(createError));
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (user: User, role: Role) => {
    try {
      await api.patch(`/api/users/${user.id}`, { role });
      toast.ok(`已把 ${user.name} 的角色改为${ROLE_LABELS[role]}`);
      await load();
    } catch (updateError) {
      toast.critical('修改失败', errorMessage(updateError));
    }
  };

  const toggleStatus = async (user: User) => {
    const next = user.status === 'active' ? 'suspended' : 'active';
    try {
      await api.patch(`/api/users/${user.id}`, { status: next });
      toast.ok(next === 'active' ? '账号已启用' : '账号已停用');
      await load();
    } catch (updateError) {
      toast.critical('操作失败', errorMessage(updateError));
    }
  };

  const resetPassword = async (user: User, trigger: HTMLElement) => {
    const confirmed = await overlay.confirm({
      placementKey: `user.reset.${user.id}`,
      trigger,
      title: `重置 ${user.name} 的口令？`,
      description: '会生成一个一次性口令并让该账号所有已登录设备立即失效。口令只显示这一次。',
      confirmText: '重置',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      const result = await api.post<{ email: string; temporaryPassword: string }>(`/api/users/${user.id}/password`);
      setTemporary({ email: result.email, password: result.temporaryPassword });
      await load();
    } catch (resetError) {
      toast.critical('重置失败', errorMessage(resetError));
    }
  };

  const removeUser = async (user: User, trigger: HTMLElement) => {
    const confirmed = await overlay.confirm({
      placementKey: `user.delete.${user.id}`,
      trigger,
      title: `删除账号 ${user.name}？`,
      description: '只有从未提出创意、也从未评论过的账号才能删除。有贡献的账号请改用「停用」。',
      confirmText: '删除',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/users/${user.id}`);
      toast.ok('账号已删除');
      await load();
    } catch (deleteError) {
      toast.critical('删除失败', errorMessage(deleteError));
    }
  };

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: '成员',
      render: (user) => (
        <div className="flex flex-col">
          <span className="text-xs font-medium text-fg">{user.name}</span>
          <span className="text-[11px] text-fg-subtle">{user.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: '角色',
      width: '10rem',
      render: (user) =>
        canManage && user.id !== currentUser?.id ? (
          <Select
            value={user.role}
            onChange={(event) => void changeRole(user, event.target.value as Role)}
            options={ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
          />
        ) : (
          <Badge tone={user.role === 'admin' ? 'accent' : user.role === 'reviewer' ? 'info' : 'neutral'}>{ROLE_LABELS[user.role]}</Badge>
        ),
    },
    {
      key: 'status',
      header: '状态',
      width: '7rem',
      render: (user) => (
        <div className="flex flex-col gap-1">
          <Badge tone={user.status === 'active' ? 'ok' : 'critical'}>{user.status === 'active' ? '启用' : '已停用'}</Badge>
          {user.mustChangePassword ? <Badge tone="caution">待改密</Badge> : null}
        </div>
      ),
    },
    {
      key: 'lastLoginAt',
      header: '最近登录',
      width: '9rem',
      hideOnMobile: true,
      render: (user) => (
        <span className="text-[11px] text-fg-muted" title={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : ''}>
          {user.lastLoginAt ? relativeTime(user.lastLoginAt) : '从未登录'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      width: '16rem',
      render: (user) =>
        canManage ? (
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <Button size="sm" variant="ghost" onClick={(event) => void resetPassword(user, event.currentTarget)}>
              重置口令
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void toggleStatus(user)}>
              {user.status === 'active' ? '停用' : '启用'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-critical"
              disabled={user.id === currentUser?.id}
              onClick={(event) => void removeUser(user, event.currentTarget)}
            >
              删除
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="用户管理"
        description="账号由管理员创建，没有开放注册。初始口令属于共享秘密，成员首次登录会被要求立即修改。"
        actions={canManage ? <Button variant="primary" onClick={() => setCreating(true)}>新建账号</Button> : null}
      />

      <Card>
        <CardBody className="flex flex-wrap items-center gap-2">
          <div className="w-56">
            <TextInput
              placeholder="搜索姓名或邮箱"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void load();
              }}
            />
          </div>
          <Button size="sm" variant="default" loading={loading} onClick={() => void load()}>
            查询
          </Button>
          <span className="ml-auto text-[11px] text-fg-subtle">共 {users.length} 个账号</span>
        </CardBody>
      </Card>

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      <Card>
        <CardBody className="p-0">
          {loading && users.length === 0 ? (
            <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
              <Spinner /> 正在加载账号…
            </div>
          ) : (
            <DataTable rows={users} columns={columns} getRowId={(user) => user.id} emptyTitle="没有匹配的账号" />
          )}
        </CardBody>
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="新建账号"
        description="创建后请把初始口令当面或私下告知本人；平台不会通过邮件发送口令。"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              取消
            </Button>
            <Button variant="primary" loading={submitting} onClick={() => void createUser()}>
              创建
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <TextInput label="姓名" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <TextInput
            label="邮箱"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            hint="登录名，内部使用即可"
          />
          <Select
            label="角色"
            value={form.role}
            onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
            options={ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
            hint={ROLE_DESCRIPTIONS[form.role]}
          />
          <TextInput
            label="初始口令"
            type="text"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            error={form.password === '' ? undefined : validatePassword(form.password)?.message ?? undefined}
            hint={PASSWORD_HINT}
          />
          {validateName(form.name) === null && validateEmail(form.email) === null && validatePassword(form.password) === null ? null : (
            <p className="text-[11px] text-fg-subtle">补齐姓名、邮箱与符合策略的口令后即可创建。</p>
          )}
          <div className="rounded-md border border-line bg-surface-2 p-2.5">
            <p className="text-[11px] font-medium text-fg-muted">该角色拥有的权限</p>
            <ul className="mt-1 flex flex-wrap gap-1">
              {permissionsForRole(form.role).map((permission: Permission) => (
                <li key={permission}>
                  <Badge tone="neutral">{PERMISSION_LABELS[permission]}</Badge>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>

      <Modal
        open={temporary !== null}
        onClose={() => setTemporary(null)}
        title="一次性口令"
        description="这个口令只显示这一次，关闭后无法再次查看。对方首次登录会被要求立即修改。"
        size="sm"
        footer={
          <Button variant="primary" onClick={() => setTemporary(null)}>
            我已记下
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <p className="text-xs text-fg-muted">{temporary?.email}</p>
          <code className="code-block rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-fg">{temporary?.password}</code>
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              void navigator.clipboard
                .writeText(temporary?.password ?? '')
                .then(() => toast.ok('已复制'))
                .catch(() => toast.caution('复制失败', '请手动选择文本复制。'));
            }}
          >
            复制口令
          </Button>
        </div>
      </Modal>

      {users.length === 0 && !loading ? (
        <Card>
          <CardBody>
            <EmptyState title="还没有账号" description="平台首次启动会自动创建初始管理员（见部署文档的环境变量）。" />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle subtitle="三层，没有上下级汇报关系">角色说明</CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="flex flex-col gap-2">
            {ROLES.map((role) => (
              <li key={role} className="flex flex-wrap items-baseline gap-2 text-[11px]">
                <Badge tone={role === 'admin' ? 'accent' : role === 'reviewer' ? 'info' : 'neutral'}>{ROLE_LABELS[role]}</Badge>
                <span className="text-fg-muted">{ROLE_DESCRIPTIONS[role]}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </PageContainer>
  );
}
